// src/pages/dashboard/BranchMonthlyPerformanceOverallDashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Calendar,
  Download,
  Filter,
  Layers,
  Loader2,
  Table2,
  BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { helper } from "@/lib/helper";

type RecoveryColor = "red" | "yellow" | "green";

type Row = {
  month: string;
  issued_loans: number;
  disbursed: number;
  expected: number;
  join: number;
  processing: number;
  insurance_fees: number;
  admin_fees: number;
  interest_fees: number;
  penalties: number;
  total_collected: number;
  recovery: number;
  recovery_color: RecoveryColor;
  gross_revenue: number;
  expenses: number;
  net_revenue: number;
};

type Totals = Omit<Row, "month">;

type BranchOpt = { id: number; name: string };

type KPI = { label: string; value: number };
type TrendPoint = { month: string; disbursed: number; collected: number; defaulted: number };
type DistributionItem = { name: string; value: number };

type MetaResp = {
  title: string;
  branches: BranchOpt[];
  defaults?: { months?: number; branch?: number | null; year?: number; month?: string };
};

type CardsResp = {
  kpis: KPI[];
  totals: Totals | null;
};

type GraphsResp = {
  trend: TrendPoint[];
  distribution: DistributionItem[];
};

type RowsResp = {
  rows: Row[];
  totals?: Totals;
};

const COLORS = ["hsl(210 80% 55%)", "hsl(142 70% 50%)", "hsl(0 80% 60%)", "hsl(30 90% 55%)"];

const fmt = (n: any) => new Intl.NumberFormat("en-KE").format(Number(n || 0));
const ksh = (n: any) => `KES ${fmt(n)}`;

const fmtMoneyShort = (n: number) => {
  const v = Number(n || 0);
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return `${v}`;
};

function recoveryBadge(color: RecoveryColor) {
  if (color === "green") return "bg-emerald-500/15 text-emerald-700 border-emerald-500/25";
  if (color === "yellow") return "bg-amber-500/15 text-amber-700 border-amber-500/25";
  return "bg-rose-500/15 text-rose-700 border-rose-500/25";
}

function buildParams(months: number, branch: string, year: number, month: string) {
  const p = new URLSearchParams();
  p.set("months", String(months));
  if (branch !== "All") p.set("branch", branch);
  p.set("year", String(year));
  p.set("month", month);
  return p.toString();
}

function KPIChip({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card className="rounded-2xl shadow-sm border bg-card">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-xl border bg-muted/30 p-2">{icon}</div>
          <div className="min-w-0">
            <div className="text-xs md:text-sm text-muted-foreground">{label}</div>
            <div className="text-lg md:text-2xl font-semibold tracking-tight truncate">{value}</div>
            {sub ? <div className="text-xs text-muted-foreground mt-1">{sub}</div> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonBlock({ h = 14 }: { h?: number }) {
  return <div className="w-full rounded-xl bg-muted/30 animate-pulse" style={{ height: h }} />;
}

function SectionLoader({ label }: { label: string }) {
  return (
    <Card className="rounded-2xl border shadow-sm">
      <CardContent className="p-8">
        <div className="flex items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">{label}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton({ height = 300 }: { height?: number }) {
  return <div className="rounded-2xl bg-muted/20 animate-pulse w-full" style={{ height }} />;
}

function DataRowCard({ r }: { r: Row }) {
  return (
    <Card className="rounded-2xl border shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{r.month}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Issued: <span className="font-medium text-foreground">{fmt(r.issued_loans)}</span>
              <span className="mx-2">•</span>
              Recovery:{" "}
              <Badge className={cn("px-2 py-0.5 border", recoveryBadge(r.recovery_color))}>
                {Number(r.recovery || 0).toFixed(2)}%
              </Badge>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 rounded-xl">
            Net: <span className="ml-1 font-semibold text-primary">{fmtMoneyShort(r.net_revenue || 0)}</span>
          </Badge>
        </div>

        <Separator className="my-3" />

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Disbursed</div>
            <div className="font-semibold">{ksh(r.disbursed)}</div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Collected</div>
            <div className="font-semibold">{ksh(r.total_collected)}</div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Expected</div>
            <div className="font-semibold">{ksh(r.expected)}</div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Gross Revenue</div>
            <div className="font-semibold">{ksh(r.gross_revenue)}</div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Penalties</div>
            <div className="font-semibold">{ksh(r.penalties)}</div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground">Expenses</div>
            <div className="font-semibold">{ksh(r.expenses)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BranchMonthlyPerformanceOverallDashboard() {
  const [activeTab, setActiveTab] = useState<"table" | "cards" | "graphs">("table");

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<string>("All");
  const [branch, setBranch] = useState<string>("All");
  const [months, setMonths] = useState<number>(6);

  const [metaLoading, setMetaLoading] = useState(false);
  const [title, setTitle] = useState("All Branch Monthly Performance Overall");
  const [branches, setBranches] = useState<BranchOpt[]>([]);

  const [cardsLoading, setCardsLoading] = useState(false);
  const [graphsLoading, setGraphsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [cardsLoadedOnce, setCardsLoadedOnce] = useState(false);
  const [graphsLoadedOnce, setGraphsLoadedOnce] = useState(false);
  const [rowsLoadedOnce, setRowsLoadedOnce] = useState(false);

  const [kpisRaw, setKpisRaw] = useState<KPI[]>([]);
  const [cardsTotals, setCardsTotals] = useState<Totals | null>(null);

  const [trendRaw, setTrendRaw] = useState<TrendPoint[]>([]);
  const [distributionRaw, setDistributionRaw] = useState<DistributionItem[]>([]);

  const [rows, setRows] = useState<Row[]>([]);
  const [rowsTotals, setRowsTotals] = useState<Totals | null>(null);

  const monthOptions = useMemo(
    () => [
      { value: "All", label: "All Months (Selected Year)" },
      { value: "01", label: "January" },
      { value: "02", label: "February" },
      { value: "03", label: "March" },
      { value: "04", label: "April" },
      { value: "05", label: "May" },
      { value: "06", label: "June" },
      { value: "07", label: "July" },
      { value: "08", label: "August" },
      { value: "09", label: "September" },
      { value: "10", label: "October" },
      { value: "11", label: "November" },
      { value: "12", label: "December" },
    ],
    []
  );

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => now - i);
  }, []);

  const monthsOptions = useMemo(() => [3, 6, 7, 12, 18, 24], []);

  const loadMeta = async () => {
    setMetaLoading(true);
    try {
      const res = await apiFetch<any>("/dashboard/meta");
      const payload: MetaResp = res?.data ?? res;

      setTitle(payload?.title ?? "All Branch Monthly Performance Overall");
      setBranches(Array.isArray(payload?.branches) ? payload.branches : []);

      const d = payload?.defaults;
      if (d?.branch !== undefined && d?.branch !== null) setBranch(String(d.branch));
      if (d?.year) setYear(Number(d.year));
      if (d?.month) setMonth(String(d.month));
    } catch (e) {
      console.error(e);
      setBranches([]);
    } finally {
      setMetaLoading(false);
    }
  };

  const loadCards = async () => {
    setCardsLoading(true);
    try {
      const qs = buildParams(months, branch, year, month);
      const res = await apiFetch<any>(`/dashboard/cards?${qs}`);
      const payload: CardsResp = res?.data ?? res;

      setKpisRaw(Array.isArray(payload?.kpis) ? payload.kpis : []);
      setCardsTotals(payload?.totals ?? null);
    } catch (e) {
      console.error(e);
      setKpisRaw([]);
      setCardsTotals(null);
    } finally {
      setCardsLoading(false);
    }
  };

  const loadGraphs = async () => {
    setGraphsLoading(true);
    try {
      const qs = buildParams(months, branch, year, month);
      const res = await apiFetch<any>(`/dashboard/graphs?${qs}`);
      const payload: GraphsResp = res?.data ?? res;

      setTrendRaw(Array.isArray(payload?.trend) ? payload.trend : []);
      setDistributionRaw(Array.isArray(payload?.distribution) ? payload.distribution : []);
    } catch (e) {
      console.error(e);
      setTrendRaw([]);
      setDistributionRaw([]);
    } finally {
      setGraphsLoading(false);
    }
  };

  const loadRows = async () => {
    setRowsLoading(true);
    try {
      const qs = buildParams(months, branch, year, month);
      const res = await apiFetch<any>(`/dashboard/rows?${qs}`);
      const payload: RowsResp = res?.data ?? res;

      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
      setRowsTotals(payload?.totals ?? null);
    } catch (e) {
      console.error(e);
      setRows([]);
      setRowsTotals(null);
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadMeta();
      setRowsLoadedOnce(true);
      await loadRows();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "cards" && !cardsLoadedOnce) {
      setCardsLoadedOnce(true);
      loadCards();
    }

    if (activeTab === "graphs" && !graphsLoadedOnce) {
      setGraphsLoadedOnce(true);
      loadGraphs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const applyCurrentTab = async () => {
    if (activeTab === "table") {
      setRowsLoadedOnce(true);
      await loadRows();
      return;
    }

    if (activeTab === "cards") {
      setCardsLoadedOnce(true);
      await loadCards();
      return;
    }

    if (activeTab === "graphs") {
      setGraphsLoadedOnce(true);
      await loadGraphs();
    }
  };

  const applyAll = async () => {
    if (rowsLoadedOnce) await loadRows();
    if (cardsLoadedOnce) await loadCards();
    if (graphsLoadedOnce) await loadGraphs();
  };

  const anyLoading = metaLoading || cardsLoading || graphsLoading || rowsLoading;

  const chartTrendData = useMemo(() => {
    return trendRaw.map((t) => ({
      month: t.month,
      disbursed: Math.round((t.disbursed || 0) / 1_000_000),
      collected: Math.round((t.collected || 0) / 1_000_000),
      defaulted: Math.round((t.defaulted || 0) / 1_000_000),
    }));
  }, [trendRaw]);

  const pieData = useMemo(() => {
    if (distributionRaw.length) return distributionRaw;
    return [
      { name: "Active", value: 0 },
      { name: "Closed", value: 0 },
      { name: "Defaulted", value: 0 },
      { name: "Pending", value: 0 },
    ];
  }, [distributionRaw]);

  const kpis = useMemo(() => {
    const sourceTotals = cardsTotals ?? rowsTotals;

    if (kpisRaw.length) {
      const mapVal = (label: string, v: number) => {
        const l = label.toLowerCase();
        if (l.includes("disbursed") || l.includes("collected")) return `KES ${fmtMoneyShort(v)}`;
        if (l.includes("rate")) return `${Number(v || 0).toFixed(2)}%`;
        return fmt(v);
      };

      return kpisRaw.map((k) => ({
        label: k.label,
        value: mapVal(k.label, k.value),
      }));
    }

    return [
      { label: "Total Disbursed", value: `KES ${fmtMoneyShort(sourceTotals?.disbursed || 0)}` },
      { label: "Total Collected", value: `KES ${fmtMoneyShort(sourceTotals?.total_collected || 0)}` },
      { label: "Recovery", value: `${Number(sourceTotals?.recovery || 0).toFixed(2)}%` },
      { label: "Issued Loans", value: fmt(sourceTotals?.issued_loans || 0) },
    ];
  }, [kpisRaw, cardsTotals, rowsTotals]);

  const exportCSV = () => {
    const totals = rowsTotals;

    const header = [
      "Month",
      "Issued Loans",
      "Disbursed",
      "Expected",
      "Join",
      "Processing",
      "Insurance Fees",
      "Admin Fees",
      "Interest Fees",
      "Penalties",
      "Total Collected",
      "Recovery",
      "Gross Revenue",
      "Expenses",
      "Net Revenue",
    ];

    const body = rows.map((r) => [
      r.month,
      r.issued_loans,
      r.disbursed,
      r.expected,
      r.join,
      r.processing,
      r.insurance_fees,
      r.admin_fees,
      r.interest_fees,
      r.penalties,
      r.total_collected,
      r.recovery,
      r.gross_revenue,
      r.expenses,
      r.net_revenue,
    ]);

    const footer = totals
      ? [
          "Totals",
          totals.issued_loans,
          totals.disbursed,
          totals.expected,
          totals.join,
          totals.processing,
          totals.insurance_fees,
          totals.admin_fees,
          totals.interest_fees,
          totals.penalties,
          totals.total_collected,
          totals.recovery,
          totals.gross_revenue,
          totals.expenses,
          totals.net_revenue,
        ]
      : null;

    const csv = [header, ...body, ...(footer ? [footer] : [])]
      .map((row) => row.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "branch_monthly_performance_overall.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <div className="space-y-5 md:space-y-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight truncate">{title}</h1>
                {anyLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              <p className="text-sm text-muted-foreground">
                Faster loading with tabs. Cards are used for mobile and iPad Air, full table only on large desktop.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={applyCurrentTab} disabled={anyLoading}>
                {anyLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Filter className="h-4 w-4 mr-2" />}
                Apply Current Tab
              </Button>
              <Button variant="outline" size="sm" onClick={applyAll} disabled={anyLoading}>
                Refresh Loaded Tabs
              </Button>
              <Button variant="outline" size="sm" onClick={exportCSV} disabled={rows.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </motion.div>

        <Card className="rounded-2xl border shadow-sm">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-xl">
                  Filters
                </Badge>
                <span className="text-xs text-muted-foreground hidden sm:inline">Apply to all tabs</span>
              </div>
              <Badge variant="outline" className="rounded-xl text-xs">
                {branch === "All" ? "All Branches" : `Branch #${branch}`} • {months} months
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Year</div>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Month</div>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Branch</div>
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue placeholder="--All--" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">--All--</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Window</div>
                <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthsOptions.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m} months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "table" | "cards" | "graphs")} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 rounded-2xl">
            <TabsTrigger value="table" className="rounded-xl">Table</TabsTrigger>
            <TabsTrigger value="cards" className="rounded-xl">Cards</TabsTrigger>
            <TabsTrigger value="graphs" className="rounded-xl">Graphs</TabsTrigger>
          </TabsList>

          <TabsContent value="table" className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="rounded-xl">
                <Table2 className="h-3.5 w-3.5 mr-1" />
                Last {months} Months Performance
              </Badge>
              <Badge variant="outline" className="rounded-xl text-xs">
                Rows: {rows.length}
              </Badge>
            </div>

            {/* Mobile + iPad Air + tablet = cards */}
            <div className="grid grid-cols-1 gap-3 xl:hidden">
              {rowsLoading ? (
                <SectionLoader label="Loading performance cards..." />
              ) : rows.length === 0 ? (
                <Card className="rounded-2xl border shadow-sm">
                  <CardContent className="p-4 text-sm text-muted-foreground">No data found</CardContent>
                </Card>
              ) : (
                <>
                  {rows.map((r, idx) => (
                    <motion.div key={`${r.month}-${idx}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                      <DataRowCard r={r} />
                    </motion.div>
                  ))}

                  {rowsTotals && (
                    <Card className="rounded-2xl border shadow-sm bg-muted/20">
                      <CardContent className="p-4">
                        <div className="font-semibold mb-3">Totals</div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-xl border bg-background p-3">
                            <div className="text-xs text-muted-foreground">Disbursed</div>
                            <div className="font-semibold">{ksh(rowsTotals.disbursed)}</div>
                          </div>
                          <div className="rounded-xl border bg-background p-3">
                            <div className="text-xs text-muted-foreground">Collected</div>
                            <div className="font-semibold">{ksh(rowsTotals.total_collected)}</div>
                          </div>
                          <div className="rounded-xl border bg-background p-3">
                            <div className="text-xs text-muted-foreground">Expected</div>
                            <div className="font-semibold">{ksh(rowsTotals.expected)}</div>
                          </div>
                          <div className="rounded-xl border bg-background p-3">
                            <div className="text-xs text-muted-foreground">Net Revenue</div>
                            <div className="font-semibold text-primary">{ksh(rowsTotals.net_revenue)}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>

            {/* Desktop only */}
            <Card className="rounded-2xl border shadow-sm hidden xl:block">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Monthly Performance Table</CardTitle>
              </CardHeader>

              <CardContent className="p-0 relative">
                {rowsLoading && (
                  <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[1px] flex items-center justify-center rounded-2xl">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-sm font-medium">Loading table...</span>
                    </div>
                  </div>
                )}

                
                  <div className="relative w-full max-w-full overflow-hidden">
  {/* Scroll shadows */}
  <div className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-background to-transparent z-30" />
  <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-background to-transparent z-30" />

  <div className="w-full overflow-x-auto rounded-lg border border-border">
    <Table className=" w-max text-sm">
      
      {/* ✅ Sticky Header */}
      <TableHeader className="sticky top-0 z-20 bg-background">
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableHead className="text-sm min-w-[80px] whitespace-nowrap sticky left-0 z-30 bg-background border-r border-border">
            Month
          </TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Issued</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Disbursed</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Expected</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Join</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Processing</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Insurance</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Admin</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Interest</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Penalties</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Collected</TableHead>
          <TableHead className="text-center text-sm min-w-[130px] whitespace-nowrap">Recovery</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Gross Rev</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Expenses</TableHead>
          <TableHead className="text-right text-sm min-w-[130px] whitespace-nowrap">Net Rev</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {!rowsLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={15} className="text-center py-10 text-muted-foreground">
              No data found
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r, idx) => (
            <TableRow key={`${r.month}-${idx}`} className={cn(idx % 2 === 0 && "bg-muted/10")}>

              {/* ✅ Sticky Month Column */}
              <TableCell className="font-medium whitespace-nowrap sticky left-0 z-20 bg-background border-r border-border">
                {helper.formatMonthShort(r.month)}
              </TableCell>

              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.issued_loans)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.disbursed)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.expected)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.join)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.processing)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.insurance_fees)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.admin_fees)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.interest_fees)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.penalties)}</TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold whitespace-nowrap">{fmt(r.total_collected)}</TableCell>

              <TableCell className="text-center whitespace-nowrap">
                <Badge className={cn("px-2 py-0.5 border", recoveryBadge(r.recovery_color))}>
                  {Number(r.recovery || 0).toFixed(2)}%
                </Badge>
              </TableCell>

              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.gross_revenue)}</TableCell>
              <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(r.expenses)}</TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold text-primary whitespace-nowrap">
                {fmt(r.net_revenue)}
              </TableCell>
            </TableRow>
          )
        ))}

        {/* ✅ Totals Row */}
        {!rowsLoading && rowsTotals && (
          <TableRow className="bg-muted/40 hover:bg-muted/40 font-semibold border-t">
            <TableCell className="font-bold whitespace-nowrap sticky left-0 z-30 bg-background border-r border-border">
              Totals
            </TableCell>

            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.issued_loans)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.disbursed)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.expected)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.join)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.processing)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.insurance_fees)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.admin_fees)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.interest_fees)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.penalties)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.total_collected)}</TableCell>

            <TableCell className="text-center whitespace-nowrap">
              <Badge className={cn("px-2 py-0.5 border", recoveryBadge(rowsTotals.recovery_color))}>
                {Number(rowsTotals.recovery || 0).toFixed(2)}%
              </Badge>
            </TableCell>

            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.gross_revenue)}</TableCell>
            <TableCell className="text-right font-mono text-sm whitespace-nowrap">{fmt(rowsTotals.expenses)}</TableCell>
            <TableCell className="text-right font-mono text-sm text-primary whitespace-nowrap">
              {fmt(rowsTotals.net_revenue)}
            </TableCell>
          </TableRow>
        )}
      </TableBody>

    </Table>
  </div>
</div>
                 
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cards" className="space-y-4">
            {cardsLoading ? (
              <>
                <SectionLoader label="Loading KPI cards..." />
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  <Card className="rounded-2xl"><CardContent className="p-5 space-y-2"><SkeletonBlock h={12} /><SkeletonBlock h={28} /></CardContent></Card>
                  <Card className="rounded-2xl"><CardContent className="p-5 space-y-2"><SkeletonBlock h={12} /><SkeletonBlock h={28} /></CardContent></Card>
                  <Card className="rounded-2xl"><CardContent className="p-5 space-y-2"><SkeletonBlock h={12} /><SkeletonBlock h={28} /></CardContent></Card>
                  <Card className="rounded-2xl"><CardContent className="p-5 space-y-2"><SkeletonBlock h={12} /><SkeletonBlock h={28} /></CardContent></Card>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  <KPIChip icon={<BarChart3 className="h-4 w-4" />} label={kpis[0]?.label ?? "Total Disbursed"} value={kpis[0]?.value ?? "-"} />
                  <KPIChip icon={<Layers className="h-4 w-4" />} label={kpis[1]?.label ?? "Total Collected"} value={kpis[1]?.value ?? "-"} />
                  <KPIChip icon={<Calendar className="h-4 w-4" />} label={kpis[2]?.label ?? "Recovery"} value={kpis[2]?.value ?? "-"} />
                  <KPIChip icon={<Building2 className="h-4 w-4" />} label={kpis[3]?.label ?? "Issued Loans"} value={kpis[3]?.value ?? "-"} />
                </div>

                {cardsTotals && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <Card className="rounded-2xl border shadow-sm">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Total Disbursed</div>
                        <div className="text-xl font-semibold truncate">{ksh(cardsTotals.disbursed)}</div>
                      </CardContent>
                    </Card>
                    <Card className="rounded-2xl border shadow-sm">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Total Collected</div>
                        <div className="text-xl font-semibold truncate">{ksh(cardsTotals.total_collected)}</div>
                      </CardContent>
                    </Card>
                    <Card className="rounded-2xl border shadow-sm">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Gross Revenue</div>
                        <div className="text-xl font-semibold truncate">{ksh(cardsTotals.gross_revenue)}</div>
                      </CardContent>
                    </Card>
                    <Card className="rounded-2xl border shadow-sm">
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">Net Revenue</div>
                        <div className="text-xl font-semibold text-primary truncate">{ksh(cardsTotals.net_revenue)}</div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="graphs" className="space-y-4">
            {graphsLoading && <SectionLoader label="Loading charts..." />}

            <div className="grid grid-cols-1 2xl:grid-cols-12 gap-4 md:gap-6">
              <Card className="rounded-2xl border shadow-sm 2xl:col-span-8">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Loan Performance Trends
                  </CardTitle>
                  <div className="text-xs text-muted-foreground">
                    Values displayed in <span className="font-medium">Millions</span>.
                  </div>
                </CardHeader>
                <CardContent>
                  {graphsLoading ? (
                    <ChartSkeleton height={320} />
                  ) : (
                    <div className="h-[260px] sm:h-[300px] md:h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartTrendData}>
                          <defs>
                            <linearGradient id="colorDisbursed" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(262 80% 55%)" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="hsl(262 80% 55%)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(260 20% 30% / 0.15)" />
                          <XAxis dataKey="month" tickMargin={8} />
                          <YAxis tickFormatter={(v) => `${v}M`} width={48} />
                          <Tooltip />
                          <Legend />
                          <Area type="monotone" dataKey="disbursed" stroke="hsl(262 80% 55%)" fill="url(#colorDisbursed)" name="Disbursed (M)" />
                          <Line type="monotone" dataKey="collected" stroke="hsl(142 70% 50%)" strokeWidth={2} name="Collected (M)" />
                          <Line type="monotone" dataKey="defaulted" stroke="hsl(0 80% 60%)" strokeWidth={2} name="Defaulted (M)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-2xl border shadow-sm 2xl:col-span-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    Loan Distribution
                  </CardTitle>
                  <div className="text-xs text-muted-foreground">Current portfolio status mix.</div>
                </CardHeader>
                <CardContent>
                  {graphsLoading ? (
                    <ChartSkeleton height={280} />
                  ) : (
                    <div className="h-[240px] md:h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={4} dataKey="value">
                            {pieData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {!graphsLoading && (
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      {pieData.map((d) => (
                        <div key={d.name} className="rounded-xl border bg-muted/10 p-2">
                          <div className="text-muted-foreground">{d.name}</div>
                          <div className="font-semibold">{fmt(d.value)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}